import React, {useCallback, useEffect, useMemo, useState} from 'react';
import { ColDef, SortChangedEvent } from 'ag-grid-community';
import { createStyles, makeStyles } from '@material-ui/core';
import AdminList, {
	ArticleViewCellRenderer,
	NameCellRenderer,
	ReadOnlyArticleViewCellRenderer,
	ReadOnlyLabelCellRenderer
} from '../../ui/admin/AdminList';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import {
	Article,
	ArticleDisplay,
	Channel,
	ChannelOwner,
	CurrentUserDetails,
	Desk,
	Publisher,
	User
} from '../../../shared/types';
import ArticleFilter from '../../ui/admin/ArticleFilter';
import { Chip, ExtendedTheme, Link } from 'neon-design-system';
import { ArticleSelectionKind, ArticleState, ResearchRole } from '../../../shared/global-enums';
import { ARTICLES } from '../../../gql/articles';
import { DESKS_SHORT } from '../../../gql/desks';
import { GET_CHANNELS_PUBLISHERS_DASHBOARD_SHORT } from '../../../gql/channels';
import { formatDisplayDate } from '../../../utils/date.utils';
import { Filter, FilterType, useArticleFilter}  from '../../providers/admin/ArticleFilterProvider';
import { calculatePremiumExpiryDate } from '../../../utils/premium-utils';
import { useUser } from '../../providers/UserProvider';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { usePrevious, usersContains } from '../../../utils/utils';
import { debounce } from 'lodash';
import { Sorting } from '../../../utils/ag-grid.utils';

const name = 'Articles';
const apiName = 'articles';

const useStyles = makeStyles((theme: ExtendedTheme) => createStyles({
	chipPanel: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2)
	},
	chipItem: {
		marginRight: theme.spacing(1)
	},
	entityLinkList: {
		width: '100%'
	},
	title: {
		display: 'block',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis'
	}
}));

const canAdministerArticle = (
	premiumChannel: any,
	contributors: any,
	desksData: any,
	channelsData: any,
	publishedBy: Publisher,
	isAdmin: boolean,
	userDetails: CurrentUserDetails | undefined
): boolean => {
	const desksContributors = publishedBy?.type === 'desk' ?
		desksData?.documents.find((d: Desk) => d._id === publishedBy?._id)?.contributors : [];

	const channelAdmins = premiumChannel
		? channelsData?.channels.find((c: Channel) => c._id === premiumChannel)?.adminUsers
		: [];

	return !!(isAdmin ||
		usersContains(channelAdmins, userDetails?.id || '') ||
		usersContains(contributors || [], userDetails?.id || '') ||
		usersContains(desksContributors, userDetails?.id || ''));
}

const deskOwnsChannel = (desk: Desk, channel: Channel): boolean => desk._id === channel.ownerId;

const isContributorToDesk = (desk: Desk, userDetails: CurrentUserDetails | undefined): boolean =>
	desk
		.contributors
		.map((deskMember: User) => deskMember._id)
		.includes(userDetails?.id ?? '');

const ownsChannel = (desksData: any, channel: Channel, userDetails: CurrentUserDetails | undefined): boolean => {
	switch (channel.ownerType) {
	case ChannelOwner.contributor:
		return channel.owner._id === userDetails?.id;
	case ChannelOwner.desk:
		return !!(desksData?.documents.find((desk: Desk) =>
			deskOwnsChannel(desk, channel) &&
			isContributorToDesk(desk, userDetails)));
	default:
		return false;
	}
};

const canViewStatsForArticleWithinChannel = (
	channelId: any,
	desksData: any,
	channelsData: any,
	isAdmin: boolean,
	userDetails: CurrentUserDetails | undefined,
	isPremium: boolean
): boolean => {
	if (!isPremium) return false;
	if (isAdmin) return true;

	const channel: Channel | undefined = channelsData?.channels.find((c: Channel) => c._id === channelId);

	if (!channel) return false; // user doesn't have access to channel as channel is not pulled in a list

	if (ownsChannel(desksData, channel, userDetails)) return true;
	if (usersContains(channel.adminUsers, userDetails?.id || '')) return true;

	return false;
}

const canViewStatsForArticleOutsideChannel = (
	desksData: any,
	publishedBy: Publisher,
	isAdmin: boolean,
	userDetails: CurrentUserDetails | undefined,
	isPremium: boolean
): boolean => {
	if (!isPremium || !publishedBy || publishedBy?.type !== ChannelOwner.desk) return false;
	if (isAdmin) return true;

	const desk: Desk = desksData?.documents.find((d: Desk) => d._id === publishedBy._id)

	if (!desk) return false;
	if (isContributorToDesk(desk, userDetails)) return true;

	return false;
}

const canViewArticleStats = (
	premiumChannel: any,
	publishedBy: Publisher,
	isPremium: boolean,
	desksData: any,
	channelsData: any,
	isAdmin: boolean,
	userDetails: CurrentUserDetails | undefined
): boolean => {
	return premiumChannel
		? canViewStatsForArticleWithinChannel(
			premiumChannel,
			desksData,
			channelsData,
			isAdmin,
			userDetails,
			isPremium)
		: canViewStatsForArticleOutsideChannel(
			desksData,
			publishedBy,
			isAdmin,
			userDetails,
			isPremium);
}

const getCategoriesFilter = (filterItems: Filter[], selectedCategory?: string) => {
	const fromFilter = filterByFilterTypes(filterItems, ['topic']) || [];
	const finalFilter = selectedCategory ? fromFilter.concat([selectedCategory]) : fromFilter;
	if (finalFilter.length === 0) return undefined;
	return finalFilter;
}

const getPublishedByFilter = (filterItems: Filter[]) => filterByFilterTypes(filterItems, ['desk', 'contributor']);
const getTagFilter = (filterItems: Filter[]) => filterByFilterTypes(filterItems, ['tag']);
const getChannelFilter = (filterItems: Filter[]) => filterByFilterTypes(filterItems, ['channel', 'archived-channel']);
const getPromotionalContentFilter = (filterItems: Filter[]) => filterByFilterTypes(filterItems, ['promotional-content']);

const filterByFilterTypes = (filterItems: Filter[], types: FilterType[]) => {
	const found = filterItems.filter(f => types.indexOf(f.type) > -1).map(f => f._id);
	if (found.length === 0) return undefined;
	return found;
}

const ArticleList = () => {
	const { userDetails, hasScope } = useUser();
	const isAdmin = [ResearchRole.Administrator].some(hasScope);
	const classes = useStyles();
	const filters = useArticleFilter();
	const [searchString, setSearchString] = useState('');
	const [articleKind, setArticleKind] = useState(ArticleSelectionKind.All);
	const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(15);
	const prevCurrentPage = usePrevious(currentPage);
	const [sorting, setSorting] = useState<Sorting>({field: 'created'});
	const [getArticles, { loading: articlesLoading, data: articlesData }] = useLazyQuery(ARTICLES, { fetchPolicy: 'network-only' });
	const { loading: desksLoading, data: allDesksData } = useQuery(DESKS_SHORT, { fetchPolicy: 'network-only' });
	const { loading: channelsLoading, data: channelsData } = useQuery(GET_CHANNELS_PUBLISHERS_DASHBOARD_SHORT, { fetchPolicy: 'network-only' });

	const articles: ArticleDisplay[] = useMemo(() => {
		if(!articlesData) return [];

		return articlesData.articles.articles.map((_: Article) => ({
			..._,
			premiumExpiresDate: !_.isPremium
				? ''
				: (_.premiumExpiryOffset === undefined || _.premiumExpiryOffset === null || _.premiumExpiryOffset === '')
					? 'No expiry'
					: 'Expires ' + calculatePremiumExpiryDate(_.publishDate ?? new Date(), _.premiumExpiryOffset).format('DD MMM YYYY HH:mm')
		}));
	}, [articlesData]);

	const total = useMemo(() => articlesData?.articles.total || 0, [articlesData, articlesLoading]);

	const callGetArticles = useCallback(() => getArticles({
		variables: {
			params: {
				type: articleKind,
				categoryId: getCategoriesFilter(filters.items, selectedCategory),
				publishedBy: getPublishedByFilter(filters.items),
				tagId: getTagFilter(filters.items),
				channel: getChannelFilter(filters.items),
				promotionalContentId: getPromotionalContentFilter(filters.items),
				searchQuery: searchString,
				sortField: sorting.field,
				sortDirection: sorting.direction,
				limit: perPage,
				offset: (currentPage - 1) * perPage
			}
		}
	}), [searchString, articleKind, selectedCategory, perPage, currentPage, sorting, filters.items]);

	useEffect(() => {
		if (filters.items.some(item => item.type === 'archived-channel')) {
			setArticleKind(ArticleSelectionKind.Archived);
		}
	}, [filters]);

	useEffect(() => {
		if (prevCurrentPage === currentPage && currentPage !== 1) {
			return setCurrentPage(1);
		}
		callGetArticles();
	}, [searchString, articleKind, selectedCategory, perPage, currentPage, sorting, filters.items]);

	useEffect(() => {
		callGetArticles();
	}, [currentPage]);

	const filtersRenderer = withOnClick(FilterRenderer, filters.add);

	const isSorted = useCallback((field: string) => {
		return sorting.field === field ? {sort: sorting.direction || 'desc'} : {};
	}, [sorting]);

	const columnDefs: ColDef[] = [{
		headerName: 'Title',
		field: 'title',
		width: 440,
		...isSorted('title'),
		cellRendererFramework: (props: any) => canAdministerArticle(
			props.data.premiumChannel,
			props.data.contributors,
			allDesksData,
			channelsData,
			props.data.publishedBy,
			isAdmin,
			userDetails)
			? <NameCellRenderer className={classes.title} {...props} apiName={apiName} />
			: <ReadOnlyLabelCellRenderer {...props} />
		,
		menuTabs: []
	},
	{
		headerName: 'Published By',
		field: 'publishedBy',
		sortable: false,
		width: 140,
		cellRendererFramework: filtersRenderer,
		tooltipField: 'publishedBy.name',
		menuTabs: []

	},
	// To be review at a later stage:
	// This causes error :Exceeded memory limit for $group, but didn't allow external sort. Pass allowDiskUse:true to opt in.
	// {
	// 	headerName: 'Total views',
	// 	field: 'nonUniqueViews',
	// 	width: 100,
	// 	valueFormatter: (params) => params.value ? formatPlural(params.value, 'view') : 'No views',
	// 	menuTabs: []
	// },
	{
		headerName: 'Unique views',
		sortable: false,
		field: 'views',
		width: 100,
		cellRendererFramework: (props: any) => canViewArticleStats(
			props.data.premiumChannel,
			props.data.publishedBy,
			props.data.isPremium,
			allDesksData,
			channelsData,
			isAdmin,
			userDetails)
			? <ArticleViewCellRenderer {...props} apiName={apiName} linkTo="articles" />
			: <ReadOnlyArticleViewCellRenderer {...props} />,
		menuTabs: []
	},
	{
		headerName: 'Published',
		field: 'publishDate',
		sortingOrder: ['desc', 'asc', 'none'],
		...isSorted('publishDate'),
		width: 110,
		valueFormatter: ({ data }) => getPublishedDate(data),
		menuTabs: []
	},
	{
		headerName: 'Last Edit',
		field: 'lastEdited',
		sortable: false,
		width: 110,
		valueFormatter: ({ data }) => getEditDate(data),
		menuTabs: []
	},
	{
		headerName: 'Premium',
		field: 'premiumExpiresDate',
		sortable: false,
		width: 160,
		menuTabs: []
	}
	];

	const loading = articlesLoading || desksLoading || channelsLoading;

	const debouncedSetQueryString = debounce(setSearchString, 500);

	return (
		<AdminList
			name={name}
			items={articles}
			itemsNumber={total}
			loading={loading}
			columns={columnDefs}
			showAdd
			showCaption={!loading && total}
			secondaryButtonLabel="New from template"
			secondaryButtonUrl={baseUrls.publisher.articles.addFromTemplate}
			aboveTable={
				<div className={classes.chipPanel}>
					{filters.items.map(f =>
						<Chip
							key={f._id}
							label={f.name || 'Anonymous'}
							className={classes.chipItem}
							onDelete={() => filters.remove(f._id)}
							styleName="default" />)}
				</div>
			}
			gridOptions={{
				onSortChanged: (e: SortChangedEvent) => {
					const sortColumn = e.columnApi.getColumnState().find(c => c.sort !== null);
					if (sortColumn) {
						setSorting({field: sortColumn.colId as string, direction: sortColumn.sort as 'asc' | 'desc'});
					} else {
						setSorting({field: 'created'});
					}
				}
			}}
			overwritePagination
			overwriteCurrentPage={currentPage}
			overwritePageRange={perPage}
			onCurrentPageChange={v => setCurrentPage(v)}
			onPerPageChange={v => setPerPage(v)}
			toolbar={
				<ArticleFilter
					articleKind={articleKind}
					searchString={searchString}
					onSelectedKindChange={setArticleKind}
					onQueryStringChange={debouncedSetQueryString}
					onSelectedCategoryChange={setSelectedCategory}
				/>
			}
		/>

	)
};

const withOnClick = <T,>(Component: React.FC<T>, handleClick: (filter: Filter) => void) => (props: T) =>
	<Component {...props} onClick={handleClick} />

interface FilterRendererProps {
	value: Filter;
	onClick: (filter: Filter) => void;
}

const FilterRenderer = ({ value, onClick }: FilterRendererProps) => {
	const classes = useStyles();

	return (
		value ? <div className={classes.entityLinkList}>
			<Link className={classes.title}
				onClick={() => onClick(value)}>{value.name}</Link>
		</div> : <></>);
};

function getPublishedDate(article: Article): string {
	if (!article || !article.publishDate || article.state !== ArticleState.Published) {
		return '';
	}
	return formatDisplayDate(article.publishDate) ?? '';
}

function getEditDate(article: Article): string {
	if (!article || !article.lastEdited) {
		return '';
	}
	return formatDisplayDate(article.lastEdited) ?? '';
}

export default ArticleList;
